import { supabase } from "@/integrations/supabase/client";

export type GoalStatus = "active" | "paused" | "achieved";

export type GoalRow = {
  id: string;
  user_id: string;
  role: string;
  category: string | null;
  rationale: string | null;
  salary_band: string | null;
  required_skills: string[];
  source_node_id: string | null;
  status: GoalStatus;
  created_at: string;
  updated_at: string;
};

export const goalsQueryKey = (userId: string) => ["goals", userId] as const;

export async function fetchGoals(userId: string): Promise<GoalRow[]> {
  const { data, error } = await supabase
    .from("goals")
    .select("*")
    .eq("user_id", userId)
    .order("created_at", { ascending: false });
  if (error) throw error;
  return (data ?? []).map((d) => ({
    ...(d as unknown as GoalRow),
    required_skills: Array.isArray((d as { required_skills?: unknown }).required_skills)
      ? ((d as { required_skills: string[] }).required_skills)
      : [],
  }));
}

export type CreateGoalInput = {
  role: string;
  category?: string | null;
  rationale?: string | null;
  salaryBand?: string | null;
  requiredSkills?: string[];
  sourceNodeId?: string | null;
};

export async function createGoal(userId: string, input: CreateGoalInput): Promise<GoalRow> {
  const { data, error } = await supabase
    .from("goals")
    .insert({
      user_id: userId,
      role: input.role,
      category: input.category ?? null,
      rationale: input.rationale ?? null,
      salary_band: input.salaryBand ?? null,
      required_skills: input.requiredSkills ?? [],
      source_node_id: input.sourceNodeId ?? null,
    } as never)
    .select("*")
    .single();
  if (error) throw error;
  return data as unknown as GoalRow;
}

export async function updateGoalStatus(id: string, status: GoalStatus) {
  const { error } = await supabase
    .from("goals")
    .update({ status } as never)
    .eq("id", id);
  if (error) throw error;
}

export async function deleteGoal(id: string) {
  const { error } = await supabase.from("goals").delete().eq("id", id);
  if (error) throw error;
}
